/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {
  Button,
  buttonVariants,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Spinner,
} from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import LogoFull from '@web/src/components/primatives/logo-full/logo-full'
import { useAuth } from '@web/src/hooks/use-auth'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'

const authInlineLegalLinkClass = 'inline font-semibold text-primary underline-offset-4 hover:underline'
const authFooterButtonClass = cn(buttonVariants({ variant: 'ghost', size: 'default' }), 'w-full flex-1')

export const Route = createFileRoute('/(public)/auth/policies-declined')({
  staticData: {
    disableSeasonalTheme: true,
  },
  component: PoliciesDeclinedPage,
  head: () =>
    seo({
      title: 'Policies declined',
      description: 'You declined the updated Spicy Friends policies and can no longer use the site.',
      robots: robotsMetaForEnvironment({
        production: 'noindex, nofollow',
        staging: 'noindex,nofollow',
      }),
    }),
})

function PoliciesDeclinedPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { currentUser, logOut } = useAuth()
  const profileId = currentUser?.profile?.id
  const isSigningOut = logOut.isPending

  const onGoToSignIn = async () => {
    if (!currentUser) {
      await navigate({ to: '/auth/login' })
      return
    }

    logOut.mutate()
  }

  return (
    <div className="relative z-10 flex justify-center py-14">
      <Card className="mx-4 w-full max-w-lg shadow-2xl">
        <CardHeader className="flex flex-col items-center gap-2 text-center">
          <LogoFull size="md" />
          <h1 className="mt-2 font-semibold text-foreground text-lg">{t('You can no longer use Spicy Friends')}</h1>
        </CardHeader>

        <CardContent className="flex flex-col gap-4 text-muted-foreground leading-relaxed">
          <p>
            {t(
              'Because you declined the updated Terms of Use and Acceptable Use Policy, you will no longer be able to use the site.'
            )}
          </p>
          <p>{t('If you would like your account removed, you can delete it from your account page.')}</p>
          <p>
            {t('If you change your mind, you can')}{' '}
            <Link className={authInlineLegalLinkClass} to="/auth/accept-policies">
              {t('accept the policies')}
            </Link>{' '}
            {t('to restore access.')}
          </p>
        </CardContent>

        <CardFooter className="flex-col gap-3 sm:flex-row">
          {profileId ? (
            <Link className={authFooterButtonClass} params={{ profileId } as never} to="/account/$profileId/general">
              {t('Delete your account?')}
            </Link>
          ) : null}
          <Button className="w-full flex-1" disabled={isSigningOut} onClick={onGoToSignIn} variant="outline">
            {isSigningOut ? <Spinner className="mr-2" /> : null}
            {t('Logout')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}
